import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Calculator, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

// Sector benchmarks for PER comparison
const SECTOR_BENCHMARKS = {
  'tech': { name: 'Technologie', perRange: [20, 35], avgPer: 28 },
  'finance': { name: 'Finance / Banques', perRange: [6, 12], avgPer: 9 },
  'energy': { name: 'Énergie', perRange: [8, 15], avgPer: 12 },
  'healthcare': { name: 'Santé', perRange: [15, 25], avgPer: 20 },
  'consumer': { name: 'Consommation', perRange: [12, 22], avgPer: 17 },
  'industrials': { name: 'Industrie', perRange: [10, 18], avgPer: 14 },
  'realestate': { name: 'Immobilier', perRange: [8, 16], avgPer: 12 },
  'utilities': { name: 'Services publics', perRange: [12, 20], avgPer: 16 },
};

const PERCalculator = () => {
  const [price, setPrice] = useState('');
  const [eps, setEps] = useState('');
  const [sector, setSector] = useState('');

  const result = useMemo(() => {
    const priceNum = parseFloat(price);
    const epsNum = parseFloat(eps);
    
    if (!priceNum || !epsNum || epsNum <= 0) {
      return null;
    }
    
    const per = priceNum / epsNum;
    const benchmark = SECTOR_BENCHMARKS[sector];
    
    let interpretation = 'normal';
    let interpretationText = 'Valorisation normale';
    let interpretationColor = 'text-yellow-400';
    let badgeColor = 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
    
    if (benchmark) {
      const [low, high] = benchmark.perRange;
      if (per < low) {
        interpretation = 'attractive';
        interpretationText = 'Potentiellement sous-évalué';
        interpretationColor = 'text-emerald-400';
        badgeColor = 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400';
      } else if (per > high) {
        interpretation = 'expensive';
        interpretationText = 'Potentiellement surévalué';
        interpretationColor = 'text-red-400';
        badgeColor = 'bg-red-500/20 border-red-500/30 text-red-400';
      }
    } else {
      // General rules without sector
      if (per < 10) {
        interpretation = 'attractive';
        interpretationText = 'Potentiellement sous-évalué';
        interpretationColor = 'text-emerald-400';
        badgeColor = 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400';
      } else if (per > 25) {
        interpretation = 'expensive';
        interpretationText = 'Potentiellement surévalué';
        interpretationColor = 'text-red-400';
        badgeColor = 'bg-red-500/20 border-red-500/30 text-red-400';
      }
    }
    
    return {
      per,
      interpretation,
      interpretationText,
      interpretationColor,
      badgeColor,
      benchmark
    };
  }, [price, eps, sector]);

  return (
    <Card className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border-emerald-500/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white">
          <Calculator className="w-5 h-5 text-emerald-400" />
          Calculateur PER
        </CardTitle>
        <p className="text-sm text-gray-400">
          Calculez le Price/Earnings Ratio et évaluez si l'action est attractivement valorisée
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Prix de l'action</label>
            <Input
              type="number"
              placeholder="Ex: 50.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Bénéfice par action (BPA)</label>
            <Input
              type="number"
              placeholder="Ex: 3.50"
              value={eps}
              onChange={(e) => setEps(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Secteur (optionnel)</label>
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Choisir un secteur" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="none" className="text-gray-400">Aucun (règles générales)</SelectItem>
                {Object.entries(SECTOR_BENCHMARKS).map(([key, val]) => (
                  <SelectItem key={key} value={key} className="text-white hover:bg-white/10">
                    {val.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {result && (
          <div className="mt-6 p-4 bg-gray-900/50 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-400">PER Calculé</p>
                <p className="text-4xl font-bold text-white">{result.per.toFixed(2)}</p>
              </div>
              <Badge className={result.badgeColor}>
                {result.interpretation === 'attractive' && <TrendingDown className="w-4 h-4 mr-1" />}
                {result.interpretation === 'expensive' && <TrendingUp className="w-4 h-4 mr-1" />}
                {result.interpretation === 'normal' && <Minus className="w-4 h-4 mr-1" />}
                {result.interpretationText}
              </Badge>
            </div>
            
            <div className="space-y-2 text-sm">
              <p className="text-gray-300">
                Vous payez <strong className="text-white">{result.per.toFixed(1)} années</strong> de bénéfices pour cette action.
              </p>
              
              {result.benchmark && (
                <div className="flex items-start gap-2 mt-3 p-3 bg-white/5 rounded-lg">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300">
                      <strong className="text-white">Benchmark {result.benchmark.name}</strong>
                    </p>
                    <p className="text-gray-400">
                      PER moyen du secteur : <span className="text-white">{result.benchmark.avgPer}</span>
                      <span className="mx-2">•</span>
                      Range typique : <span className="text-white">{result.benchmark.perRange[0]} - {result.benchmark.perRange[1]}</span>
                    </p>
                    <p className={`mt-1 ${result.interpretationColor}`}>
                      {result.per < result.benchmark.avgPer 
                        ? `${((1 - result.per / result.benchmark.avgPer) * 100).toFixed(0)}% moins cher que la moyenne du secteur`
                        : result.per > result.benchmark.avgPer
                        ? `${((result.per / result.benchmark.avgPer - 1) * 100).toFixed(0)}% plus cher que la moyenne du secteur`
                        : 'Au niveau de la moyenne du secteur'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!result && price && eps && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-400">
              Le BPA doit être positif pour calculer un PER significatif.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PERCalculator;
